import {ReactNode} from "react";
import {OtsTooltip} from "./OtsTooltip";

export type OtsSpanTone = "primary" | "success" | "warning" | "danger" | "neutral" | "violet" | "blue" | "yellow";

export type OtsSpan = {
  id: string;
  start: number;
  /** Missing for something still under way; drawn up to the end of the axis. */
  end?: number;
  tone?: OtsSpanTone;
  /** What the span is, for the tooltip and the accessible name. */
  label: string;
  detail?: ReactNode;
};

export type OtsSpanTimelineProps = {
  spans: OtsSpan[];
  /** The axis. Spans outside it are clipped. */
  start: number;
  end: number;
  ariaLabel: string;
  activeId?: string;
  /** Makes each span a button. */
  onSelect?: (id: string) => void;
  /** Axis labels for the two ends, e.g. shift start and end times. */
  startLabel?: ReactNode;
  endLabel?: ReactNode;
  className?: string;
};

const toneClasses: Record<OtsSpanTone, string> = {
  primary: "bg-[var(--ots-primary)]",
  success: "bg-[var(--ots-success)]",
  warning: "bg-[var(--ots-warning)]",
  danger: "bg-[var(--ots-error)]",
  neutral: "bg-[var(--ots-outline)]",
  violet: "bg-[var(--ots-violet)]",
  blue: "bg-[var(--ots-blue)]",
  yellow: "bg-[var(--ots-yellow)]",
};

/** Where a span sits on the axis, as percentages; exported for tests and for callers drawing their own marks. */
export const getSpanGeometry = (span: Pick<OtsSpan, "start" | "end">, start: number, end: number) => {
  const length = Math.max(end - start, 1);
  const from = Math.min(Math.max(span.start, start), end);
  const to = Math.min(Math.max(span.end ?? end, from), end);
  return {left: ((from - start) / length) * 100, width: ((to - from) / length) * 100};
};

/** A row-height strip of when things happened across a stretch of time: a day, a shift, a job. */
export const OtsSpanTimeline = ({spans, start, end, ariaLabel, activeId, onSelect, startLabel, endLabel, className}: OtsSpanTimelineProps) => (
  <div className={["flex min-w-0 items-center gap-2 text-[10px] tabular-nums text-[var(--ots-text-muted)]", className].filter(Boolean).join(" ")}>
    {startLabel != null && <span className="shrink-0">{startLabel}</span>}
    <div role="group" aria-label={ariaLabel} className="relative h-3 min-w-16 flex-1 rounded-full bg-[var(--ots-outline-variant)]/60">
      {spans.map(span => {
        const {left, width} = getSpanGeometry(span, start, end);
        const active = span.id === activeId;
        const classes = [
          "block size-full rounded-full outline-none transition-transform",
          toneClasses[span.tone ?? "primary"],
          active ? "scale-y-150 ring-2 ring-[var(--ots-surface)]" : "opacity-85 hover:opacity-100",
          onSelect ? "cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--ots-text)]" : "",
        ].join(" ");

        return <div key={span.id} className={["absolute inset-y-0 min-w-1", active ? "z-10" : ""].join(" ")} style={{left: `${left}%`, width: `${width}%`}}>
          <OtsTooltip
            density="compact"
            className="!flex size-full"
            content={<><span className="font-semibold">{span.label}</span>{span.detail != null && <><br/>{span.detail}</>}</>}>
            {onSelect
              ? <button type="button" aria-label={span.label} aria-pressed={active} data-span={span.id} className={classes} onClick={() => onSelect(span.id)}/>
              : <span role="img" aria-label={span.label} data-span={span.id} className={classes}/>}
          </OtsTooltip>
        </div>;
      })}
    </div>
    {endLabel != null && <span className="shrink-0">{endLabel}</span>}
  </div>
);
